'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { EXTERNAL } from '@/lib/site'

type Mode = 'user' | 'merchant'

const MODES: { id: Mode; label: string; target: string }[] = [
  { id: 'user', label: 'User', target: EXTERNAL.user },
  { id: 'merchant', label: 'Merchant', target: EXTERNAL.merchant },
]

export function MerchantUserSwitch({ className }: { className?: string }) {
  // The main marketing site represents the "user" surface by default.
  const [active, setActive] = useState<Mode>('user')

  function handleSwitch(mode: Mode, target: string) {
    setActive(mode)
    // Preserve the current route/scroll intent by appending the pathname to the target host.
    const path =
      typeof window !== 'undefined' ? window.location.pathname + window.location.search : ''
    const base = target.replace(/\/$/, '')
    const dest = mode === 'user' ? base + (path === '/' ? '' : path) : base + (path === '/' ? '' : path)
    // Same-window navigation — no new tab, no separate page.
    window.location.assign(dest || target)
  }

  return (
    <div
      role="tablist"
      aria-label="Choose Rombex experience"
      className={cn(
        'relative inline-flex items-center rounded-full border border-border bg-secondary/70 p-1 backdrop-blur',
        className,
      )}
    >
      {MODES.map((m) => {
        const isActive = active === m.id
        return (
          <button
            key={m.id}
            role="tab"
            aria-selected={isActive}
            onClick={() => handleSwitch(m.id, m.target)}
            className={cn(
              'relative z-10 rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
              isActive ? 'text-brand-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {isActive && (
              <motion.span
                layoutId="mode-pill"
                transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                className="absolute inset-0 -z-10 rounded-full bg-brand shadow-sm"
              />
            )}
            {m.label}
          </button>
        )
      })}
    </div>
  )
}
